import React, { useState } from 'react';
import type { MsThread } from '../../messages/types';
import { splitPatientName } from '../../messages/utils';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { MessagesMsgBubble } from './MessagesMsgBubble';

export interface MessagesChatProps {
  thread: MsThread;
  autoOn: boolean;
  setAutoOn: (v: boolean) => void;
  takenOver: boolean;
  setTakenOver: (v: boolean) => void;
  topic: string;
  /** Display name of the signed-in staff member (fallback for outbound staff bubbles). */
  currentStaffName?: string;
  onTakeOver?: () => void;
  onSend?: (text: string) => void;
}

function ChatComposer({
  thread,
  autoOn,
  locked,
  onSend,
}: {
  thread: MsThread;
  autoOn: boolean;
  locked: boolean;
  onSend?: (text: string) => void;
}) {
  const [text, setText] = useState('');
  const willTranslate = thread.lang === 'Spanish';
  const isUnicode = /[^\x00-\x7F]/.test(text);
  const limit = isUnicode ? 70 : 160;
  const parts = text.length === 0 ? 0 : Math.ceil(text.length / limit);
  const placeholder = thread.optedOut
    ? 'Patient opted out — cannot send'
    : locked
      ? 'Type your reply…'
      : autoOn
        ? 'Handled by Elva — Take over to type'
        : 'Elva paused — Take over to type';

  return (
    <div className="shrink-0 bg-[var(--cream-1)]" style={{ padding: '8px 22px 16px', flex: 'none' }}>
      {locked && willTranslate ? (
        <div
          className="flex items-center rounded-[var(--r-sm)] border text-xs text-[var(--ai-3)]"
          style={{
            gap: 8,
            padding: '7px 11px',
            marginBottom: 8,
            background: 'var(--ai-tint)',
            borderColor: 'color-mix(in srgb, var(--ai-3) 22%, transparent)',
          }}
        >
          <Icon kind="translate" size={12} />
          Type in <b>English</b> — sends to patient in <b>Spanish</b>.
        </div>
      ) : null}
      <div className={locked ? 'ms-composer-box' : 'ms-composer-box locked'}>
        <IconButton icon="attach" size={30} label="Attach" />
        <textarea
          value={text}
          onChange={(e) => {
            setText(e.target.value);
          }}
          placeholder={placeholder}
          disabled={!locked}
          rows={1}
          className="flex-1 resize-none border-0 bg-transparent outline-none disabled:cursor-not-allowed"
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 14,
            color: 'var(--ink-1)',
            minHeight: 24,
            maxHeight: 110,
            lineHeight: 1.5,
            cursor: locked ? 'text' : 'not-allowed',
          }}
        />
        <Button
          variant="primary"
          size="sm"
          icon="send"
          disabled={!locked || !text.length}
          onClick={() => {
            if (!text.trim()) return;
            onSend?.(text.trim());
            setText('');
          }}
        >
          Send
        </Button>
      </div>
      {locked && text.length > 0 ? (
        <div
          className="mt-1.5 flex items-center gap-1.5 text-[11.5px]"
          style={{ color: parts > 1 ? 'var(--gold)' : 'var(--ink-4)' }}
        >
          <span className="font-[var(--font-mono)]">
            {text.length} / {limit}
          </span>
          {parts > 1 ? (
            <>
              <span>·</span>
              <Icon kind="alert" size={10} stroke={2.4} />
              <span className="font-semibold">Sends as {parts} messages</span>
            </>
          ) : null}
        </div>
      ) : null}
      {!locked && !thread.optedOut ? (
        <div
          style={{
            fontSize: 11.5,
            color: 'var(--ink-4)',
            marginTop: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Icon kind={autoOn ? 'sparkle' : 'pause'} size={11} stroke={2} />
          {autoOn
            ? 'Elva is handling this conversation.'
            : 'Elva is paused — patient messages wait for staff or re-enable.'}
        </div>
      ) : null}
    </div>
  );
}

export function MessagesChat({
  thread,
  autoOn,
  setAutoOn,
  takenOver,
  setTakenOver,
  topic,
  currentStaffName,
  onTakeOver,
  onSend,
}: MessagesChatProps) {
  const msgs =
    topic === 'all' ? thread.messages : thread.messages.filter((m) => m.topic === topic);
  const locked = takenOver && !thread.optedOut;
  const { firstName, lastName } = splitPatientName(thread.name);

  return (
    <div
      className="flex min-h-0 flex-1 flex-col bg-[var(--cream-0)]"
      style={{ minWidth: 400 }}
    >
      {thread.optedOut ? (
        <div className="shrink-0" style={{ padding: '14px 16px 0', flex: 'none' }}>
          <div className="ms-banner warn">
            <Icon kind="shield-off" size={14} />
            <span>
              <b>Patient opted out.</b> Outbound SMS suppressed until they reply START (TCPA).
            </span>
          </div>
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1" style={{ padding: '14px 16px' }}>
        <div
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]"
        >
          <div className="h-[3px] shrink-0 bg-[var(--grad-spectrum)]" />
          <div
            className="flex flex-wrap items-center border-b border-[var(--cream-2)]"
            style={{
              padding: '15px 18px',
              gap: 15,
              background:
                'linear-gradient(118deg, color-mix(in srgb, var(--ai-3) 11%, var(--cream-1)), color-mix(in srgb, var(--ai-5) 9%, var(--cream-1)))',
            }}
          >
            <span
              className="ms-av ms-av-patient text-[15px]"
              style={{ width: 46, height: 46 }}
            >
              {thread.initials}
            </span>
            <div className="min-w-[150px] flex-1">
              <h2
                className="m-0 truncate font-[var(--font-display)] text-[23px] font-semibold leading-[1.1] tracking-[-0.025em] text-[var(--ink-0)]"
              >
                {firstName}
                {lastName ? (
                  <>
                    {' '}
                    <span className="elva-spectrum">{lastName}</span>
                  </>
                ) : null}
              </h2>
              <div
                className="font-[var(--font-mono)] text-[11.5px] text-[var(--ink-3)]"
                style={{ marginTop: 3 }}
              >
                {thread.phone}
              </div>
            </div>
            <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
              {thread.lang === 'Spanish' ? (
                <div
                  className="flex items-center rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)]"
                  style={{ gap: 6, padding: '5px 10px' }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--gold)',
                      background: 'var(--gold-tint)',
                      padding: '1px 6px',
                      borderRadius: 4,
                    }}
                  >
                    ES
                  </span>
                  <Icon kind="arrow-r" size={10} style={{ color: 'var(--ink-4)' }} />
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--success)',
                      background: 'var(--success-tint)',
                      padding: '1px 6px',
                      borderRadius: 4,
                    }}
                  >
                    EN
                  </span>
                </div>
              ) : null}
              <button
                type="button"
                disabled={takenOver || thread.optedOut}
                title={autoOn ? 'Elva auto-responding' : 'Elva paused'}
                onClick={() => {
                  setAutoOn(!autoOn);
                }}
                className="inline-flex items-center rounded-[var(--r-pill)] font-[var(--font-body)] text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                  height: 34,
                  padding: '0 12px',
                  gap: 6,
                  border: `1px solid ${autoOn ? 'color-mix(in srgb, var(--ai-3) 30%, transparent)' : 'var(--cream-shadow)'}`,
                  background: autoOn ? 'var(--ai-tint)' : 'var(--surface-mute)',
                  color: autoOn ? 'var(--ai-3)' : 'var(--ink-3)',
                  cursor: takenOver || thread.optedOut ? 'not-allowed' : 'pointer',
                  opacity: takenOver || thread.optedOut ? 0.5 : 1,
                }}
              >
                <Icon kind={autoOn ? 'sparkle' : 'pause'} size={12} stroke={2} />
                Elva {autoOn ? 'on' : 'off'}
              </button>
              {!takenOver ? (
                <Button
                  variant="danger"
                  size="sm"
                  iconLeft="headphones"
                  disabled={thread.optedOut}
                  onClick={() => {
                    setTakenOver(true);
                    onTakeOver?.();
                  }}
                >
                  Take over
                </Button>
              ) : (
                <span
                  className="inline-flex items-center rounded-[var(--r-pill)] bg-[var(--success-tint)] text-xs font-semibold text-[var(--success)]"
                  style={{ gap: 7, padding: '6px 12px' }}
                >
                  <span className="elva-live h-[7px] w-[7px]" />
                  Human in control
                </span>
              )}
              <IconButton icon="trash" size={34} label="Delete conversation" />
            </div>
          </div>

          <div
            className="cal-scroll flex min-h-0 flex-1 flex-col overflow-y-auto"
            style={{ padding: 20, gap: 14 }}
          >
            {msgs.map((m) => (
              <MessagesMsgBubble
                key={m.id}
                message={m}
                thread={thread}
                {...(currentStaffName ? { currentStaffName } : {})}
              />
            ))}
          </div>

          <ChatComposer
            thread={thread}
            autoOn={autoOn}
            locked={locked}
            {...(onSend ? { onSend } : {})}
          />
        </div>
      </div>
    </div>
  );
}
